<template>
  <div class="plu" :class="{ 'is-wall': token }">
    <div class="plu-bg" :style="bgStyle" />
    <div class="plu-shade" />
    <div class="plu-card">
      <p class="plu-kicker">工作人员上传</p>
      <h1>{{ title || '相册上传' }}</h1>
      <p v-if="slogan && !token" class="plu-slogan">{{ slogan }}</p>
      <template v-if="!token">
        <p class="plu-sub">输入本相册的上传口令，验证后即可选图上墙</p>
        <input v-model="password" type="password" placeholder="上传口令" class="plu-inp" @keyup.enter="auth" />
        <button class="plu-btn" :disabled="authing || !password" @click="auth">
          {{ authing ? '验证中…' : '验证口令' }}
        </button>
      </template>
      <template v-else>
        <p class="plu-ok">已通过验证。点左侧空格从相册选图，选完即上传</p>
        <div class="plu-grid">
          <label class="plu-add" :class="{ 'is-busy': uploading }">
            <span>{{ uploading ? '…' : '+' }}</span>
            <input type="file" accept="image/*" multiple :disabled="uploading" @change="onFiles" />
          </label>
          <div v-for="f in locals" :key="f.key" class="plu-cell">
            <img v-if="f.preview" :src="f.preview" alt="" />
            <div v-if="f.status === '上传中'" class="plu-pbar">
              <span class="plu-pbar__num">{{ f.progress }}%</span>
              <span class="plu-pbar__track">
                <span class="plu-pbar__fill" :style="{ width: (f.progress || 0) + '%' }" />
              </span>
            </div>
            <span v-else class="plu-badge" :class="localClass(f.status)">
              <em>{{ f.status }}</em>
            </span>
          </div>
          <template v-for="row in timeline">
            <div v-if="row.kind === 'day'" :key="row.key" class="plu-day">{{ row.label }}</div>
            <div v-else :key="row.key" class="plu-cell">
              <img :src="row.thumb" alt="" />
              <span class="plu-badge" :class="statusClass(row.status)">
                <em>{{ row.time }}</em>
                <em>{{ row.statusText }}</em>
              </span>
            </div>
          </template>
        </div>
        <p v-if="!loadingMine && !photos.length && !locals.length" class="plu-empty">还没有上传过照片</p>
        <button v-if="hasMore" class="plu-more" :disabled="loadingMine" @click="loadMore">
          {{ loadingMine ? '加载中…' : '更早的照片' }}
        </button>
      </template>
      <p v-if="msg" class="plu-msg">{{ msg }}</p>
    </div>
  </div>
</template>

<script>
import { getPhotoLiveAlbum, photoLiveUploadAuth, photoLiveStaffUpload, photoLiveUploadFile, photoLiveStaffMine } from '@/api/photoLive'

function pickUrls(res) {
  if (!res) return { originalUrl: '', displayUrl: '', thumbUrl: '' }
  if (typeof res === 'string') {
    return { originalUrl: res, displayUrl: res, thumbUrl: res }
  }
  const originalUrl = res.originalUrl || res.url || ''
  return {
    originalUrl,
    displayUrl: res.displayUrl || originalUrl,
    thumbUrl: res.thumbUrl || originalUrl,
    width: res.width,
    height: res.height
  }
}

function parseTime(v) {
  if (v == null || v === '') return null
  if (typeof v === 'number') return new Date(v)
  const d = new Date(String(v).replace(/-/g, '/'))
  return isNaN(d.getTime()) ? null : d
}

function pad(n) {
  return n < 10 ? '0' + n : '' + n
}

function dayLabel(d) {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const that = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diff = Math.round((start - that) / 86400000)
  if (diff === 0) return '今天'
  if (diff === 1) return '昨天'
  return (d.getMonth() + 1) + '月' + d.getDate() + '日'
}

function statusText(status) {
  if (status === 1) return '已通过'
  if (status === 2) return '待审'
  if (status === 0) return '已隐藏'
  return '处理中'
}

export default {
  name: 'PhotoLiveUpload',
  data() {
    return {
      albumCode: '',
      coverImg: '',
      slogan: '',
      password: '',
      token: '',
      title: '',
      authing: false,
      uploading: false,
      loadingMine: false,
      locals: [],
      photos: [],
      page: 0,
      total: 0,
      msg: ''
    }
  },
  computed: {
    bgStyle() {
      return this.coverImg ? { backgroundImage: 'url(' + this.coverImg + ')' } : {}
    },
    hasMore() {
      return this.photos.length < this.total
    },
    timeline() {
      const rows = []
      let lastDay = ''
      this.photos.forEach(p => {
        if (p.day !== lastDay) {
          rows.push({ kind: 'day', key: 'd-' + p.day + '-' + p.id, label: p.day })
          lastDay = p.day
        }
        rows.push({ kind: 'photo', key: 'p-' + p.id, ...p })
      })
      return rows
    }
  },
  created() {
    this.albumCode = this.$route.params.albumCode || this.$route.query.albumCode || ''
    this.loadAlbum()
    const saved = this.readToken()
    if (saved) {
      this.token = saved
      this.loadMine(true)
    }
  },
  methods: {
    tokenKey() {
      return 'photoLiveUploadToken:' + this.albumCode
    },
    readToken() {
      try {
        return sessionStorage.getItem(this.tokenKey()) || ''
      } catch (e) {
        return ''
      }
    },
    writeToken(token) {
      try {
        if (token) sessionStorage.setItem(this.tokenKey(), token)
        else sessionStorage.removeItem(this.tokenKey())
      } catch (e) { /* 隐私模式忽略 */ }
    },
    dropToken(message) {
      this.token = ''
      this.writeToken('')
      this.photos = []
      this.msg = message || '请重新输入口令'
    },
    async loadAlbum() {
      if (!this.albumCode) return
      try {
        const album = await getPhotoLiveAlbum(this.albumCode)
        this.coverImg = (album && album.coverImg) || ''
        this.slogan = (album && album.slogan) || ''
        if (album && album.title) this.title = album.title
      } catch (e) {
        // 相册信息只用于展示，失败仍可输口令
      }
    },
    statusClass(status) {
      if (status === 1) return 'is-ok'
      if (status === 2) return 'is-ing'
      if (status === 0) return 'is-hide'
      return ''
    },
    localClass(status) {
      if (status === '失败' || status === '无 URL') return 'is-bad'
      return 'is-ing'
    },
    mapPhoto(p) {
      const d = parseTime(p.createTime) || new Date()
      return {
        id: p.id,
        thumb: p.thumbUrl || p.displayUrl || '',
        status: p.status,
        statusText: statusText(p.status),
        time: pad(d.getHours()) + ':' + pad(d.getMinutes()),
        day: dayLabel(d),
        ts: d.getTime()
      }
    },
    async loadMine(reset) {
      if (!this.token) return
      this.loadingMine = true
      const page = reset ? 0 : this.page
      try {
        const res = await photoLiveStaffMine(this.token, page, 60)
        const list = ((res && res.content) || []).map(this.mapPhoto)
        this.total = (res && res.totalElements) || list.length
        this.photos = reset ? list : this.photos.concat(list)
        this.page = page + 1
      } catch (e) {
        const text = (e && (e.msg || e.message)) || '加载失败'
        if (/凭证|口令|过期/.test(text)) this.dropToken(text)
        else this.msg = text
      } finally {
        this.loadingMine = false
      }
    },
    loadMore() {
      this.loadMine(false)
    },
    async auth() {
      this.authing = true
      this.msg = ''
      try {
        const res = await photoLiveUploadAuth({ albumCode: this.albumCode, password: this.password })
        this.token = res.uploadToken
        this.title = res.title
        this.writeToken(this.token)
        this.loadMine(true)
      } catch (e) {
        this.msg = (e && (e.msg || e.message)) || '口令错误'
      } finally {
        this.authing = false
      }
    },
    async onFiles(e) {
      const list = Array.from((e.target && e.target.files) || [])
      if (e.target) e.target.value = ''
      if (!list.length || this.uploading) return
      this.uploading = true
      this.msg = ''
      const batch = list.map((file, i) => ({
        key: 'l-' + Date.now() + '-' + i,
        file,
        preview: URL.createObjectURL(file),
        status: '上传中',
        progress: 0
      }))
      this.locals = batch.concat(this.locals)
      for (const item of batch) {
        try {
          const fd = new FormData()
          fd.append('file', item.file)
          fd.append('uploadToken', this.token)
          const up = await photoLiveUploadFile(fd, e => {
            if (!e || !e.total) return
            const p = Math.round((e.loaded / e.total) * 100)
            // 传到 99% 为止，等入库完成再视作 100%
            item.progress = p > 99 ? 99 : p
          })
          item.progress = 100
          const urls = pickUrls(up)
          if (!urls.originalUrl) {
            item.status = '无 URL'
            this.msg = '上传未返回地址'
            continue
          }
          await photoLiveStaffUpload({
            albumCode: this.albumCode,
            uploadToken: this.token,
            originalUrl: urls.originalUrl,
            displayUrl: urls.displayUrl,
            thumbUrl: urls.thumbUrl,
            width: urls.width,
            height: urls.height
          })
          item.status = '已上传'
        } catch (err) {
          const text = (err && (err.msg || err.message)) || '上传失败'
          item.status = '失败'
          this.msg = text
          if (/凭证|口令|过期/.test(text)) {
            this.dropToken(text)
            break
          }
        }
      }
      this.locals = this.locals.filter(f => f.status !== '已上传')
      this.uploading = false
      if (this.token) this.loadMine(true)
    }
  }
}
</script>

<style scoped>
/* px 按 750 设计宽书写，构建时自动转 vw，视觉尺寸约为数值的一半 */
.plu {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 32px;
  box-sizing: border-box;
  background: #f7f5f2;
}
.plu.is-wall {
  align-items: stretch;
  justify-content: stretch;
  padding: 32px;
}
.plu.is-wall .plu-card {
  max-width: none;
  min-height: calc(100vh - 64px);
  box-sizing: border-box;
}
.plu-bg {
  position: fixed;
  inset: 0;
  background: #f7f5f2 center/cover no-repeat;
  filter: blur(48px);
  transform: scale(1.15);
}
.plu-shade {
  position: fixed;
  inset: 0;
  background: linear-gradient(180deg, rgba(120, 85, 45, 0.10), rgba(60, 40, 20, 0.32));
}
.plu-card {
  position: relative;
  width: 100%;
  max-width: 660px;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 32px;
  padding: 56px 40px 44px;
  box-shadow: 0 24px 64px rgba(60, 40, 20, 0.18);
  text-align: center;
}
.plu-kicker {
  margin: 0;
  color: #b08b57;
  font-size: 24px;
  letter-spacing: 6px;
}
.plu-card h1 {
  margin: 16px 0 0;
  font-size: 44px;
  color: #1f1a17;
  letter-spacing: 2px;
  font-weight: 700;
  font-family: "Songti SC", "Noto Serif CJK SC", "STSong", serif;
}
.plu-slogan, .plu-sub, .plu-ok {
  margin: 16px 0 0;
  color: #9c9188;
  font-size: 26px;
  line-height: 1.6;
}
.plu-ok { color: #6d8f5a; }
.plu-inp {
  width: 100%;
  height: 100px;
  margin-top: 40px;
  padding: 0 28px;
  box-sizing: border-box;
  border: 2px solid #e6dccf;
  border-radius: 24px;
  background: #fbf8f4;
  font-size: 34px;
  letter-spacing: 8px;
  text-align: center;
  color: #1f1a17;
  outline: none;
}
.plu-inp:focus { border-color: #c7a06a; }
.plu-btn {
  width: 100%;
  height: 100px;
  margin-top: 28px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #cbab7d, #a97f4e);
  color: #fff;
  font-size: 34px;
  font-weight: 600;
  letter-spacing: 4px;
  box-shadow: 0 12px 28px rgba(120, 85, 45, 0.3);
}
.plu-btn:disabled { opacity: 0.5; }
.plu-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-top: 28px;
  text-align: left;
}
.plu-add, .plu-cell {
  position: relative;
  width: 100%;
  padding-top: 100%;
  border-radius: 20px;
  overflow: hidden;
  background: #efe8e1;
  box-shadow: 0 6px 18px rgba(70, 45, 25, 0.08);
}
.plu-add {
  border: 2px dashed #d9c3a3;
  background: #fbf8f4;
  box-shadow: none;
  cursor: pointer;
}
.plu-add span {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b08b57;
  font-size: 64px;
  font-weight: 300;
  line-height: 1;
}
.plu-add input { display: none; }
.plu-add.is-busy { opacity: 0.55; pointer-events: none; }
.plu-cell img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.plu-pbar {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: rgba(35, 25, 15, 0.42);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.plu-pbar__num {
  color: #fff;
  font-size: 30px;
  letter-spacing: 1px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.plu-pbar__track {
  width: 66%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.3);
  overflow: hidden;
}
.plu-pbar__fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #ead3a8, #c7a06a);
  box-shadow: 0 0 8px rgba(255, 225, 180, 0.6);
  transition: width 0.25s linear;
}
.plu-day {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 4px 0;
  color: #9c9188;
  font-size: 24px;
}
.plu-day::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #eee6db;
}
.plu-badge {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(40, 25, 15, 0.5);
  color: #fff;
  font-size: 20px;
  font-style: normal;
}
.plu-badge em { font-style: normal; }
.plu-badge.is-ok { background: rgba(96, 140, 84, 0.78); }
.plu-badge.is-ing { background: rgba(180, 130, 50, 0.78); }
.plu-badge.is-hide { background: rgba(90, 85, 80, 0.7); }
.plu-badge.is-bad { background: rgba(170, 60, 50, 0.8); }
.plu-empty { margin: 24px 0 0; color: #b1a79d; font-size: 24px; }
.plu-more {
  margin-top: 28px;
  height: 72px;
  padding: 0 44px;
  border: 1px solid #e6dccf;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  color: #8a6b42;
  font-size: 26px;
}
.plu-msg { margin: 20px 0 0; color: #c0503f; font-size: 24px; }
</style>
